import axios from "axios";
import authHeader from "../../helpers/header";
import { connect, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation } from "react-router-dom";
import { setMessage } from "../../redux/actions/message";

import AuthLayout from "../../components/AuthLayout/AuthLayout";
import { API_URL, MEDIA_URL } from "../../configs";

function ResourcesTags(props) {
    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        watch,
        formState: { errors },
    } = useForm();

    const { message, messageType } = props;

    const dispatch = useDispatch();
    const search = useLocation().search;
    const searchParams = new URLSearchParams(search);

    const [loading, setLoading] = useState(false);
    const [resourcesData, setResourceData] = useState([]);

    // Function to fetch resource data
    const fetchResourcesData = async (ids) => {
        try {
            const res = await axios.get(`${API_URL}resources/getbulk/?ids=${ids}`);
            if (res.status === 200) {
                setResourceData(res.data);
            }
        } catch (error) {
            console.error("Error fetching resources data:", error);
        }
    };

    // Submit updated tags to the backend
    const handleUpdate = async () => {
        setLoading(true);
        try {
            const payload = {
                resource_ids: getValues("resource_ids").split(","),
                tags: getValues("new_tags").split(",").filter((tag) => tag.trim() !== ""),
                action: "add", // Specify the action as "add"
            };

            const response = await axios.put(`${API_URL}resources/bulkUpdateTags`, payload, {
                headers: authHeader(),
            });

            if (response.status === 200) {
                dispatch(setMessage("Tags have been updated successfully", "success"));
                setValue("new_tags", "");
                // Re-fetch the resource data to update the view
                const ids = getValues("resource_ids");
                await fetchResourcesData(ids);
            } else {
                dispatch(setMessage(response.data.message || "Failed to update tags", "danger"));
            }
        } catch (error) {
            console.error("Error updating tags:", error);
            dispatch(
                setMessage(
                    error.response?.data?.message || "Error updating tags. Please try again.",
                    "danger"
                )
            );
        } finally {
            setLoading(false);
        }
    };

    // Remove tags from the backend
    const handleRemove = async () => {
        setLoading(true);
        try {
            const payload = {
                resource_ids: getValues("resource_ids").split(","),
                tags: getValues("new_tags").split(",").filter((tag) => tag.trim() !== ""),
                action: "remove", // Specify the action as "remove"
            };

            const response = await axios.put(`${API_URL}resources/bulkUpdateTags`, payload, {
                headers: authHeader(),
            });

            if (response.status === 200) {
                dispatch(setMessage("Tags have been removed successfully", "success"));
                setValue("new_tags", "");
                // Re-fetch the resource data to update the view
                const ids = getValues("resource_ids");
                await fetchResourcesData(ids);
            } else {
                dispatch(setMessage(response.data.message || "Failed to remove tags", "danger"));
            }
        } catch (error) {
            console.error("Error removing tags:", error);
            dispatch(
                setMessage(
                    error.response?.data?.message || "Error removing tags. Please try again.",
                    "danger"
                )
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const ids = searchParams.get("resources_id");
        setValue("resource_ids", ids);
        fetchResourcesData(ids);
    }, []);

    return (
        <AuthLayout>
            <header className={"d-flex justify-content-between align-items-center mb-4"}>
                <h2 className="fs-5">{"Tags update"}</h2>
            </header>
            {message && (
                <div className="mb-4">
                    <div className={`alert alert-${messageType}`} role="alert">
                        {message}
                    </div>
                </div>
            )}
            <div className="row">
                <div className="col-md-6">
                    {resourcesData.map((data, index) => (
                        <div key={index} className="row mb-4">
                            <div className="col-1">
                                <img
                                    className="img-thumbnail"
                                    src={`${MEDIA_URL}${data.coverImages.path}`}
                                    width="200"
                                    alt="Resource"
                                />
                            </div>
                            <div className="col-10">
                                <p className="text-left mt-0 mb-2 py-0">
                                    <Link
                                        to={`/resources/view/${data._id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {data.name}
                                    </Link>
                                </p>
                                <p className="text-muted text-left mb-2 py-0">
                                    {data.tags.map((tag, i) => {
                                        // Watch the "new_tags" field for changes
                                        const enteredTags = watch("new_tags", "").split(",").map((t) => t.trim().toLowerCase());
                                        const isHighlighted = enteredTags.includes(tag.name.toLowerCase());

                                        return (
                                            <span
                                                key={i}
                                                className={`me-2 my-1 rounded-pill d-inline-block fw-normal py-0 px-2 ${
                                                    isHighlighted ? "bg-primary text-white" : "bg-secondary bg-opacity-10 text-secondary"
                                                }`}
                                            >
                                                {tag.name}
                                            </span>
                                        );
                                    })}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="col-md-6">
                    <div style={{ position: 'relative' }}>
                        {loading && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                background: 'rgba(255,255,255,0.7)',
                                zIndex: 10,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 8
                            }}>
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )}
                        <form>
                            <div className="mb-3">
                                <label htmlFor="tags" className="form-label">
                                    Enter tags (comma-separated)
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    {...register("new_tags", { required: true })}
                                    placeholder="Enter tags separated by commas"
                                />
                                {errors.new_tags && (
                                    <span className="small text-danger">This field is required</span>
                                )}
                            </div>
                            <input type={"hidden"} {...register("resource_ids")} />
                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleUpdate}
                                    disabled={loading}
                                >
                                    {loading ? "Updating..." : "Update"}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleRemove}
                                    disabled={loading}
                                >
                                    {loading ? "Removing..." : "Remove"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </AuthLayout>
    );
}

function mapStateToProps(state) {
    const { token } = state.auth;
    const { message, messageType } = state.message;
    return {
        token,
        message,
        messageType,
    };
}

export default connect(mapStateToProps)(ResourcesTags);
